import {
  Brand,
  MarketingCampaign,
  MarketingCampaignStep,
  Practice,
} from '@principle-theorem/principle-core';
import {
  IBrand,
  ICustomReport,
  IMarketingCampaign,
  IMarketingCampaignStep,
  IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  CustomReportTemplate,
  ICustomReportTemplate,
} from '@principle-theorem/reporting';
import {
  AtLeast,
  DocumentReference,
  Firestore,
  Region,
  Transaction,
  WithRef,
  asReffable,
  resolveSequentially,
  toTimestamp,
  uid,
} from '@principle-theorem/shared';

export interface IMarketingCampaignTemplate {
  name: string;
  description: string;
  campaign: IMarketingCampaign;
  steps: IMarketingCampaignStep[];
  regions: Region[];
  report?: ICustomReportTemplate;
}

export class MarketingCampaignTemplate {
  static async createCampaignFromTemplate(
    template: IMarketingCampaignTemplate,
    settings: AtLeast<IMarketingCampaign, 'practiceRef'>,
    creator: WithRef<IStaffer>
  ): Promise<DocumentReference<IMarketingCampaign>> {
    return Firestore.runTransaction(async (transaction) => {
      const brandRef = Practice.brandRef(asReffable(settings.practiceRef));
      const customReportRef = await MarketingCampaignTemplate.createReport(
        template,
        brandRef,
        creator,
        transaction
      );
      const campaignRef = await MarketingCampaignTemplate.createCampaign(
        template,
        settings,
        brandRef,
        customReportRef,
        transaction
      );
      await MarketingCampaignTemplate.addSteps(
        campaignRef,
        template.steps,
        transaction
      );
      return campaignRef;
    });
  }

  static async createReport(
    template: IMarketingCampaignTemplate,
    brandRef: DocumentReference<IBrand>,
    creator: WithRef<IStaffer>,
    transaction?: Transaction
  ): Promise<DocumentReference<ICustomReport> | undefined> {
    if (!template.report) {
      return;
    }
    return CustomReportTemplate.createReportFromTemplate(
      brandRef,
      template.report,
      creator,
      transaction
    );
  }

  static async createCampaign(
    template: IMarketingCampaignTemplate,
    settings: AtLeast<IMarketingCampaign, 'practiceRef'>,
    brandRef: DocumentReference<IBrand>,
    customReportRef?: DocumentReference<ICustomReport>,
    transaction?: Transaction
  ): Promise<DocumentReference<IMarketingCampaign>> {
    const campaign = MarketingCampaign.init({
      ...template.campaign,
      ...settings,
      customReportRef,
      brandRef,
    });
    campaign.nextRunAt =
      template.campaign.nextRunAt ??
      (await MarketingCampaign.getNextRunDate(campaign, toTimestamp()));
    return Firestore.addDoc(
      Brand.marketingCampaignCol(asReffable(brandRef)),
      campaign,
      undefined,
      transaction
    );
  }

  static async addSteps(
    campaignRef: DocumentReference<IMarketingCampaign>,
    stepTemplates: IMarketingCampaignStep[],
    transaction?: Transaction
  ): Promise<DocumentReference<IMarketingCampaignStep>[]> {
    const stepRefs = await resolveSequentially(stepTemplates, (stepTemplate) =>
      Firestore.addDoc(
        MarketingCampaign.marketingCampaignStepCol(asReffable(campaignRef)),
        MarketingCampaignStep.init({ ...stepTemplate, uid: uid() }),
        undefined,
        transaction
      )
    );
    await Firestore.patchDoc(campaignRef, { steps: stepRefs }, transaction);
    return stepRefs;
  }
}
